import React from 'react'
import { RichTextEditor, ToggleSwitch } from '@enamelai/design-system'
import { IAiReceptionistSettings } from '@dataTypes/ai.type'

const APPROVED_SERVICES_PLACEHOLDER = [
  "Only discuss dental services approved by the clinic. When a patient requests an appointment, identify the appropriate appointment type, check availability, and assist with booking if permitted. For procedures requiring a dentist's evaluation, offer a consultation rather than directly booking the procedure. Do not promise treatment or availability without confirmation.",
].join('\n')

const INSURANCE_PLACEHOLDER = [
  'We accept selected dental insurance plans. Our team can help confirm whether the specific plan is accepted. Coverage depends on the insurance plan and the treatment required.',
  'We offer a membership program with selected preventive care benefits and discounts.',
  "Membership and insurance combination rules depend on the clinic's program.",
].join('\n')

const LOCATION_PLACEHOLDER = [
  'Location: 123 Red Ave, Jacksonville, FL.',
  'Operating Hours: Monday to Friday from 10 AM till 6 PM and Saturday 10 AM till 2 PM.',
  '',
  'We offer free patient parking in designated spaces near the clinic entrance.',
  'Step-free entry and wheelchair-accessible facilities are available. Patients may request additional assistance before visiting.',
].join('\n')

const RESTRICTED_PLACEHOLDER = [
  'Do not promise services or appointments that the clinic does not offer.',
  'Do not attempt to diagnose emergencies; provide approved urgent-care guidance.',
  'Do not provide professional legal or individualized financial advice.',
  'Do not disclose protected patient information without proper verification and authorization.',
  'Do not guarantee coverage, claims, or reimbursements.',
  'Do not invent prices, discounts, or payment arrangements.',
  'Do not promise treatment outcomes or recovery times.',
  'Do not prescribe medication or recommend individualized dosages.',
  'Do not prescribe or select specific treatments for a patient.',
  'Do not diagnose dental conditions or interpret symptoms as a definitive diagnosis.',
].join('\n')

const TOPIC_KEYS = [
  'knowledge_approved_services',
  'knowledge_insurance_membership',
  'knowledge_location_access',
  'knowledge_restricted_actions',
] as const

const hasKnowledgeText = (html: string | null | undefined): boolean =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim().length > 0

export const knowledgeTopicSummary = (
  settings: IAiReceptionistSettings,
): { status: string; action: 'Configure' | 'Update configuration' } => {
  const written = TOPIC_KEYS.filter((key) =>
    hasKnowledgeText(settings[key]),
  ).length
  if (written === 0) {
    return {
      status: 'Add services, insurance, location, and topics to avoid.',
      action: 'Configure',
    }
  }
  const knowledgeWithheld =
    settings.knowledge_base_enabled === false &&
    (hasKnowledgeText(settings.knowledge_approved_services) ||
      hasKnowledgeText(settings.knowledge_insurance_membership) ||
      hasKnowledgeText(settings.knowledge_location_access))
  const restrictedSaved =
    hasKnowledgeText(settings.knowledge_restricted_actions) &&
    !settings.knowledge_restricted_enabled
  const status = `${written} of 4 topics written.${
    knowledgeWithheld
      ? ' Approved services, insurance, and location are saved and not used on calls or texts.'
      : ''
  }${
    restrictedSaved
      ? ' Restricted topics are saved and not used on calls or texts.'
      : ''
  }`
  return { status, action: 'Update configuration' }
}

type AiReceptionistKnowledgeProps = {
  settings: IAiReceptionistSettings
  disabled: boolean
  onChange: (next: IAiReceptionistSettings) => void
}

const editorProps = {
  variant: 'sms-notes' as const,
  bodyActions: [] as [],
  footerActions: [] as [],
  aiEnabled: false,
}

const AiReceptionistKnowledge = ({
  settings,
  disabled,
  onChange,
}: AiReceptionistKnowledgeProps): React.ReactElement => (
  <div className="ai-receptionist-knowledge">
    <div className="ai-receptionist-knowledge__restricted-title">
      <ToggleSwitch
        checked={settings.knowledge_base_enabled !== false}
        aria-label="Use knowledge base on calls and texts"
        disabled={disabled}
        onChange={(checked) =>
          onChange({ ...settings, knowledge_base_enabled: checked })
        }
      />
      <span className="ds-input-field__label">Allowed actions</span>
    </div>
    <div className="ai-receptionist-knowledge__section">
      <span className="ds-input-field__label">Approved services</span>
      <RichTextEditor
        {...editorProps}
        value={settings.knowledge_approved_services}
        placeholder={APPROVED_SERVICES_PLACEHOLDER}
        disabled={disabled}
        onChange={(next) =>
          onChange({ ...settings, knowledge_approved_services: next })
        }
      />
    </div>
    <div className="ai-receptionist-knowledge__section">
      <span className="ds-input-field__label">Insurance & Membership</span>
      <RichTextEditor
        {...editorProps}
        value={settings.knowledge_insurance_membership}
        placeholder={INSURANCE_PLACEHOLDER}
        disabled={disabled}
        onChange={(next) =>
          onChange({ ...settings, knowledge_insurance_membership: next })
        }
      />
    </div>
    <div className="ai-receptionist-knowledge__section">
      <span className="ds-input-field__label">
        Location, Parking, Accessibility & Working Hours
      </span>
      <RichTextEditor
        {...editorProps}
        value={settings.knowledge_location_access}
        placeholder={LOCATION_PLACEHOLDER}
        disabled={disabled}
        onChange={(next) =>
          onChange({ ...settings, knowledge_location_access: next })
        }
      />
    </div>
    <div className="ai-receptionist-knowledge__section">
      <div className="ai-receptionist-knowledge__restricted-title">
        <ToggleSwitch
          checked={settings.knowledge_restricted_enabled}
          aria-label="Use restricted actions on calls and texts"
          disabled={disabled}
          onChange={(checked) =>
            onChange({ ...settings, knowledge_restricted_enabled: checked })
          }
        />
        <span className="ds-input-field__label">Restricted actions</span>
      </div>
      <RichTextEditor
        {...editorProps}
        value={settings.knowledge_restricted_actions}
        placeholder={RESTRICTED_PLACEHOLDER}
        disabled={disabled}
        onChange={(next) =>
          onChange({ ...settings, knowledge_restricted_actions: next })
        }
      />
    </div>
  </div>
)

export default AiReceptionistKnowledge
